import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/3k02iwbx_btr_logo_mockup-removebg-preview.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/4o9bpmcq_BTR_Health_-_1-removebg-preview.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  // SEO
  useEffect(() => {
    document.title = 'Sign In | BTR Health - NDIS Support Platform';
  }, []);

  const from = location.state?.from?.pathname || "/dashboard";

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[login] handleSubmit v3.0 (no-credentials, hard-nav)");
    setLoading(true);

    try {
      // NOTE: omit credentials:"include" — we use Bearer tokens, not cookies.
      // iOS Safari throws DataCloneError ("object can not be cloned") on
      // credentials:include + 3rd-party cookie scenarios.
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: String(formData.email || ""),
          password: String(formData.password || ""),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }

      // Store token (strings only)
      try {
        if (data.token) localStorage.setItem("auth_token", String(data.token));
      } catch (err) { console.error("[login] localStorage", err); }

      // Update auth context (best effort — page is about to reload)
      try {
        login({
          user_id: String(data.user_id || ""),
          email: String(data.email || ""),
          name: String(data.name || ""),
          user_type: String(data.user_type || ""),
          picture: data.picture ? String(data.picture) : null,
          email_verified: Boolean(data.email_verified),
          participant_verified: Boolean(data.participant_verified),
          approval_status: data.approval_status ? String(data.approval_status) : null,
        }, data.token ? String(data.token) : null);
      } catch (err) { console.error("[login] login()", err); }

      // Hard-navigate. Avoid React Router (history.pushState) and toast
      // (BroadcastChannel) — both are DataCloneError sources on iOS Safari.
      // Routing matrix:
      //  support_worker + approved/active → /worker-dashboard
      //  support_worker + pending/null    → /worker-home (Screen 3 checklist)
      //  support_worker + submitted/verified/rejected → /worker-pending
      //  everyone else (participant)      → / (landing or the `from` they came from)
      let target = "/admin";
      if (data.user_type === "support_worker") {
        target = "/";
      }
      try { window.location.assign(target); return; } catch (_) {}
      try { window.location.href = target; return; } catch (_) {}
      try { navigate(target, { replace: true }); } catch (_) {}
    } catch (error) {
      console.error("[login] submit failed:", error);
      const raw = error instanceof Error ? error.message : String(error);
      const isCloneErr = /clone|DataClone|cloned/i.test(raw);
      const msg = isCloneErr
        ? "Almost there — please refresh the page to finish signing in."
        : raw || "Login failed. Please try again.";
      try { toast.error(msg); } catch (_) {}
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const clientId = '196494716537-4ctu733jebqktgaupg4ef372h1j322qg.apps.googleusercontent.com';
    const redirectUri = window.location.origin + '/auth/callback';
    const scope = 'openid email profile';
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>

            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <img src={LOGO_ICON} alt="BTR Health" className="h-8 w-8" />
              </div>
              <span className="text-2xl font-bold text-slate-900">BTR Health</span>
            </div>

            <h1 className="text-3xl font-bold text-slate-900 mb-2">Staff Sign In</h1>
            <p className="text-slate-600 mb-8">Admin & staff access to BTR Health</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <span className="text-sm text-slate-400" data-testid="forgot-password-hint">
                    Contact your admin
                  </span>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    className="pl-10 h-12"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#87CEEB] hover:bg-[#87CEEB]/90 text-white rounded-lg"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-slate-50 px-4 text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 rounded-lg"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>

            <p className="mt-8 text-center text-slate-500 text-sm">
              For internal BTR Health staff only.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#87CEEB] to-[#6BB8DB]">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1590905775253-a4f0f3c426ff?w=1024')] bg-cover bg-center mix-blend-overlay opacity-30" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center max-w-md">
            <h2 className="text-4xl font-bold mb-4">Support that gets you</h2>
            <p className="text-xl text-white/80">
              Connect with verified local NDIS support workers who understand your needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
